/**
 * workspaceTransition.ts — the SEALED organization-switch flow (owner
 * directive 2026-08-19: interaction-blocking announcement, "display
 * information before the official switch … is too early"; REVISED 2026-08-20:
 * "faster but just fast enough for the user to understand what is going on" —
 * the floor dropped 3000→1200ms and now OVERLAPS the switch work instead of
 * padding on top of it; blocking/non-dismissible behavior unchanged).
 *
 * ONE controller for EVERY way an organization change starts — a foreign-
 * workspace thread open (any path, via the thread store's central guard),
 * the header WorkspacePill (dormant rows included), the login card, the
 * Organization page, the new-org onboarding launch. The sequence:
 *
 *   1. The full-screen blocking overlay goes up IMMEDIATELY on the conscious
 *      click (rendered by WorkspaceTransitionHost, which subscribes here).
 *   2. The MIN_VISIBLE_MS floor starts at once; the roster read (dormant
 *      detection) and then the server-verified switch run UNDER it. Total
 *      pre-navigation hold = max(real work, floor) — the announcement is
 *      readable but never padded.
 *   3. Success: every "arrive in the right org" marker is written BEFORE the
 *      hard navigation — pending-thread-open (thread-driven switches), the
 *      justJoinedOrg greeting, the cache write-through active flag, the
 *      device's last-workspace memory, and the BOOT-CONTINUATION marker that
 *      lets the destination page keep the same overlay up until the new
 *      workspace has actually resolved. No mixed-org frame can ever paint.
 *      (public/index.html's splash reads the same marker for visual
 *      continuity across the hard navigation.)
 *   4. Failure: the overlay flips to a quiet error beat ("you're right where
 *      you were") and releases after ERROR_VISIBLE_MS. Nothing was written,
 *      so nothing changed.
 *
 * Module-level store + subscribe (activeWorkspace.ts pattern) so the store
 * guard, chrome components, and pages can all start a transition without
 * owning overlay state. Leaf-ish by design: imports only sibling leaf
 * services + the rbac callable wrapper.
 */

import { switchActiveWorkspace } from './rbacService';
import { inCurrentTree } from '../design/previewPath';
import { activatePersonalWorkspaceCore } from './personalWorkspace';
import { fetchWorkspaceOptionsCached, markActiveWorkspaceInCache } from './workspaceOptionsCache';
import { workspaceLabel } from './workspaceIdentity';
import { writeConfirmedWorkspace } from './prest0nRuntime/confirmedWorkspaceCache';
import { savePendingThreadOpen } from '../lib/pendingThreadOpen';
import { writeTabWorkspacePin } from './prest0nRuntime/tabWorkspace';
import { saveJustJoinedOrg } from '../lib/justJoinedOrg';

/**
 * The owner-directed floor (2026-08-20: "faster but just fast enough to
 * understand" — was 3000). Starts at begin() and overlaps the real work.
 */
export const MIN_VISIBLE_MS = 1200;
/** Error beat: failure copy is a full sentence — it keeps reading time. */
export const ERROR_VISIBLE_MS = 3000;
/** Boot-continuation marker TTL — a stale marker must never brick a boot. */
export const BOOT_MARKER_TTL_MS = 2 * 60 * 1000;
/** Hard ceiling for the post-reload continuation overlay (resolve watchdog). */
export const BOOT_CONTINUATION_MAX_MS = 6_000;
/** Beat after the destination workspace resolves — lets the first correctly-keyed frame land under the overlay. */
export const BOOT_RELEASE_BEAT_MS = 350;
/** Fade-out duration on boot release (the pre-nav side never fades — the page hard-navigates under it). */
export const BOOT_RELEASE_FADE_MS = 180;

const BOOT_MARKER_KEY = 'prest0_ws_transition_v1';

export interface WorkspaceTransitionRequest {
  /** The organization being entered. */
  targetAccountId: string;
  /**
   * Display name for the announcement. Optional — when absent the controller
   * resolves it from the cached workspace list (never a raw account id; the
   * overlay shows a generic line until the name lands).
   */
  targetLabel?: string;
  /** Signed-in uid (unlocks cache write-through + last-workspace memory). */
  uid?: string | null;
  /** Thread-driven switches: the foreign thread being opened. */
  threadId?: string;
  threadTitle?: string;
  /**
   * Where the switch lands: 'reload' keeps the user on the page they are on
   * (pill / Organization page contract, owner directive 2026-08-16);
   * 'desktop' is the thread-open / login-card landing.
   */
  navigation: 'reload' | 'desktop';
  /**
   * Optional same-origin landing override, honored only when
   * navigation === 'desktop' and the path starts with '/' (the onboarding
   * launch's hire_required → /subscription case). Dormant targets always
   * land on /onboarding regardless.
   */
  navigationPath?: string;
}

export interface WorkspaceTransitionState {
  request: WorkspaceTransitionRequest;
  phase: 'switching' | 'error';
  startedAtMs: number;
  /** Resolved display label (request.targetLabel, or looked up async). */
  label: string | null;
}

type Listener = (state: WorkspaceTransitionState | null) => void;

let current: WorkspaceTransitionState | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of Array.from(listeners)) {
    try { l(current); } catch { /* a listener failure never breaks the flow */ }
  }
}

export function subscribeWorkspaceTransition(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getWorkspaceTransitionState(): WorkspaceTransitionState | null {
  return current;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

/**
 * Boot-continuation marker: written right before the hard navigation so the
 * destination page re-raises the same overlay until the new workspace
 * resolves. sessionStorage — a transition belongs to ONE tab.
 */
export interface BootTransitionMarker {
  targetAccountId: string;
  label: string | null;
  at: number;
}

export function stampBootTransition(targetAccountId: string, label: string | null): void {
  try {
    const marker: BootTransitionMarker = { targetAccountId, label, at: Date.now() };
    sessionStorage.setItem(BOOT_MARKER_KEY, JSON.stringify(marker));
  } catch { /* storage unavailable — the destination just boots normally */ }
}

export function readBootTransition(): BootTransitionMarker | null {
  try {
    const raw = sessionStorage.getItem(BOOT_MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BootTransitionMarker;
    if (!parsed || typeof parsed.targetAccountId !== 'string' || !parsed.targetAccountId) {
      sessionStorage.removeItem(BOOT_MARKER_KEY);
      return null;
    }
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > BOOT_MARKER_TTL_MS) {
      sessionStorage.removeItem(BOOT_MARKER_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearBootTransition(): void {
  try { sessionStorage.removeItem(BOOT_MARKER_KEY); } catch { /* noop */ }
}

/**
 * Begin the sealed switch. Idempotent while a transition is in flight (the
 * overlay is already blocking — a second click cannot exist, but a
 * programmatic double-call must not double-navigate).
 */
export function beginWorkspaceTransition(request: WorkspaceTransitionRequest): void {
  const target = (request.targetAccountId || '').trim();
  if (!target || current) return;

  current = {
    request,
    phase: 'switching',
    startedAtMs: Date.now(),
    label: (request.targetLabel || '').trim() || null,
  };
  emit();

  // Label lookup (async, non-blocking): never a raw account id on screen —
  // the overlay shows a generic announcement until the name lands.
  if (!current.label) {
    void fetchWorkspaceOptionsCached(request.uid ?? null)
      .then((options) => {
        const opt = options.find((o) => o.accountId === target);
        if (opt && current && current.request.targetAccountId === target) {
          current = { ...current, label: workspaceLabel(opt, request.uid) };
          emit();
        }
      })
      .catch(() => undefined);
  }

  void (async () => {
    // The floor starts NOW — it overlaps the roster read AND the switch
    // (2026-08-20 revision). Total pre-nav hold = max(real work, floor),
    // never roster + max(switch, floor).
    const floor = delay(MIN_VISIBLE_MS);

    // DORMANT PERSONAL WORKSPACE (owner incident 2026-08-19: "couldn't
    // switch — the organization change didn't go through"): a dormant row
    // is SYNTHESIZED with no account root doc, and the plain verified
    // switch provably refuses it (account_not_found → permission-denied).
    // The lazy-workspace contract says the first click ACTIVATES instead
    // (trial + structure, then the verified switch) — previously only the
    // header pill and the Organization page special-cased this, so the
    // extended thread rail and every other sealed-switch caller hit the
    // refusal. The decision now lives at this chokepoint: every surface
    // that begins a transition inherits it. Roster lookup failure falls
    // back to the plain switch — exactly the old behavior.
    let dormantTarget = false;
    try {
      const options = await fetchWorkspaceOptionsCached(request.uid ?? null);
      dormantTarget = options.some((o) => o.accountId === target && o.dormant === true);
    } catch { /* unknown roster — plain switch below */ }

    let switched = false;
    // A rescue that ACTIVATED the personal workspace must land on
    // /onboarding like any dormant first entry (set below).
    let activatedDormant = dormantTarget;
    try {
      const [switchResult] = await Promise.all([
        dormantTarget
          ? activatePersonalWorkspaceCore(target)
          : switchActiveWorkspace(target),
        floor,
      ]);
      void switchResult;
      switched = true;
    } catch {
      // RESIDUAL "couldn't switch" rescue (task #33, 2026-08-20) — one
      // bounded second pass, because the first failure is frequently not a
      // real refusal:
      //  (a) transient (network/cold start), and — since the server now
      //      completes a switch even when its cosmetic follow-ups fail — a
      //      retry lands on the already-moved pointer and succeeds;
      //  (b) dormant misdetection: the roster read above failed, a dormant
      //      SELF row took the plain switch, and account_not_found came
      //      back — the retry ladder below ends in activation, which is
      //      what that row needed all along.
      try {
        await (dormantTarget ? activatePersonalWorkspaceCore(target) : switchActiveWorkspace(target));
        switched = true;
      } catch {
        if (!dormantTarget && request.uid && target === request.uid) {
          try {
            await activatePersonalWorkspaceCore(target);
            activatedDormant = true;
            switched = true;
          } catch {
            switched = false;
          }
        } else {
          switched = false;
        }
      }
    }

    if (!current || current.request.targetAccountId !== target) return;

    if (!switched) {
      current = { ...current, phase: 'error' };
      emit();
      await delay(ERROR_VISIBLE_MS);
      current = null;
      emit();
      return;
    }

    const label = current.label;
    // Every arrival marker BEFORE the navigation — the destination boots
    // already knowing (and showing) where it is.
    if (request.threadId) {
      savePendingThreadOpen(request.threadId);
    }
    if (label) {
      try { saveJustJoinedOrg(label); } catch { /* greeting is optional */ }
    }
    markActiveWorkspaceInCache(request.uid ?? null, target);
    // PER-TAB ORGANIZATION (2026-09-11): this tab chose it, so this tab holds
    // it — a switch on another device can no longer move this page. The
    // account-wide pointer still moved (switchActiveWorkspace above), which is
    // what brings a NEW tab or device back here.
    writeTabWorkspacePin(target, request.uid ?? null);
    // switchActiveWorkspace succeeded — the server confirmed the new active
    // org, which qualifies as a strict confirmation for the sticky cache.
    writeConfirmedWorkspace(request.uid ?? null, target);
    stampBootTransition(target, label);

    if (activatedDormant) {
      // Activation contract (lazy personal workspace): the first entry
      // lands on the uniform staged onboarding, never straight on the
      // desktop of a just-minted workspace.
      window.location.assign(inCurrentTree('/onboarding'));
    } else if (request.navigation === 'reload') {
      window.location.reload();
    } else {
      const path = request.navigationPath;
      window.location.assign(inCurrentTree(path && path.startsWith('/') ? path : '/desktop'));
    }
  })();
}

/** Test seam: drop any in-flight transition state (jsdom suites). */
export function __resetWorkspaceTransitionForTests(): void {
  current = null;
}
